import React, { useEffect, useState } from 'react';
import LoadError from '../../components/LoadError';
import { formatDateTime } from '../../lib/utils';
import { apiGet, apiPost } from '../../contexts/AuthContext';
import { MessageSquare, Plus, X, Send, Clock, Reply } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

export default function PartnerMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ subject: '', message: '' });

  const fetchMessages = () => {
    setLoadError(false);
    apiGet('/partner/messages')
      .then(r => {
        setMessages(r.data);
        window.dispatchEvent(new CustomEvent('xp:refresh-notifications'));
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchMessages(); }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) {
      toast.error('Please fill in subject and message');
      return;
    }
    setSubmitting(true);
    try {
      await apiPost('/partner/messages', form);
      toast.success('Message sent successfully');
      setDialogOpen(false);
      setForm({ subject: '', message: '' });
      fetchMessages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-brand border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in-up">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-ember tracking-tight" style={{ fontFamily: 'Outfit' }}>Messages</h1>
            <p className="text-ink-2 mt-1">Send messages to admin and view your message history</p>
          </div>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-brand hover:bg-brand-soft text-white">
          <Plus className="w-4 h-4 mr-2" /> New Message
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-surface/70 backdrop-blur-xl rounded-xl border border-contrast/10 p-4">
          <p className="text-xs text-ink-3 font-medium uppercase tracking-wider">Total Sent</p>
          <p className="text-2xl font-bold text-ink mt-1">{messages.length}</p>
        </div>
        <div className="bg-surface/70 backdrop-blur-xl rounded-xl border border-contrast/10 p-4">
          <p className="text-xs text-ink-3 font-medium uppercase tracking-wider">Replied</p>
          <p className="text-2xl font-bold text-success mt-1">{messages.filter(m => m.replies && m.replies.length > 0).length}</p>
        </div>
        <div className="bg-surface/70 backdrop-blur-xl rounded-xl border border-contrast/10 p-4">
          <p className="text-xs text-ink-3 font-medium uppercase tracking-wider">Awaiting Reply</p>
          <p className="text-2xl font-bold text-ember mt-1">{messages.filter(m => !m.replies || m.replies.length === 0).length}</p>
        </div>
      </div>

      {/* Messages List */}
      {loadError ? (
        <LoadError what="your messages" onRetry={() => { setLoading(true); fetchMessages(); }} />
      ) : messages.length === 0 ? (
        <div className="text-center py-16 bg-surface/70 backdrop-blur-xl rounded-xl border border-contrast/10">
          <MessageSquare className="w-12 h-12 text-ink-3 mx-auto mb-3" />
          <p className="text-ink-2">No messages yet. Send your first message to admin.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {messages.map(msg => (
            <div key={msg.id} className="bg-surface/70 backdrop-blur-xl rounded-xl border border-contrast/10 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-ink">{msg.subject}</h3>
                    {msg.replies && msg.replies.length > 0 ? (
                      <Badge className="bg-brand/15 text-success">Replied</Badge>
                    ) : (
                      <Badge className="bg-ember/15 text-ember">Awaiting Reply</Badge>
                    )}
                  </div>
                  <p className="text-ink-2 text-sm whitespace-pre-wrap">{msg.message}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3 text-xs text-ink-3">
                <Clock className="w-3 h-3" />
                {formatDateTime(msg.created_at)}
              </div>

              {/* Admin Replies */}
              {msg.replies && msg.replies.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-xs font-medium text-ink-2 uppercase tracking-wider flex items-center gap-1">
                    <Reply className="w-3 h-3" /> Admin Replies
                  </p>
                  {msg.replies.map(r => (
                    <div key={r.id} className="bg-brand/[0.06] rounded-lg p-3 border border-brand/20">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold text-brand">{r.admin_name}</span>
                        <span className="text-xs text-ink-3">{formatDateTime(r.created_at)}</span>
                      </div>
                      <p className="text-sm text-ink whitespace-pre-wrap">{r.reply}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* New Message Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-brand" /> Send Message to Admin
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div>
              <Label htmlFor="subject">Subject *</Label>
              <Input id="subject" name="subject" value={form.subject} onChange={handleChange} placeholder="Enter subject" />
            </div>
            <div>
              <Label htmlFor="message">Message *</Label>
              <textarea
                id="message"
                name="message"
                value={form.message}
                onChange={handleChange}
                placeholder="Type your message here..."
                rows={5}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-brand hover:bg-brand-soft text-white" disabled={submitting}>
                {submitting ? 'Sending...' : 'Send Message'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
